/**
 * @file The named query contract and `/query` wire: scope levels, periods, query ids, request, result, provenance,
 * refusal codes, route, ID-token header and refusal body. Produced by `cloud_run/analytics/src/serving`, answered by
 * its `service` and forwarded by the `queryAnalytics` callable; React and the agent tool read it too. Every request
 * field is a closed enum, so no caller string reaches SQL. A result's `level` is the granted level, its instants are
 * `Date#toISOString()` strings, and each `refusedColumns` entry is null in every row while the rest answers.
 * `AnalyticsQueryProvenance` never keeps rows. `NAMED_QUERY_METRICS` lets React group queries by metric without the
 * registry. Project stages sort lowest rank first; aging buckets run by days past due (0 to 30 includes not yet due).
 */
import type { FactTableId } from './Analytics.Catalog.types';
import type { AnalyticsParameters } from './Analytics.Parameters.types';

export const ANALYTICS_SCOPE_LEVELS = ['self', 'team', 'org'] as const;

export type AnalyticsScopeLevel = (typeof ANALYTICS_SCOPE_LEVELS)[number];

export const ANALYTICS_PERIODS = ['ytd', 'qtd', 'mtd', 'wtd', 'lastWeek', 'lastMonth'] as const;

export type AnalyticsPeriod = (typeof ANALYTICS_PERIODS)[number];

export const NAMED_QUERY_IDS = [
  'salesClosedByPerson',
  'salesClosedByTeam',
  'salesClosedTotal',
  'salesClosedByMonth',
  'bookedSeries',
  'ordersWrittenSeries',
  'quotesIssuedSeries',
  'quoteCountAndValueByKind',
  'quoteValueTotal',
  'quoteValueByMonth',
  'quoteValueByPerson',
  'quoteValueByTeam',
  'releaseValueByState',
  'openQuotesAndBuysByProject',
  'conversionByKind',
  'projectsByStage',
  'pipelineByStage',
  'orderLinesTotals',
  'orderLinesList',
  'invoiceTotals',
  'receivableAging',
  'invoiceList',
  'overdueInvoicesBySummaryScope',
] as const;

export type NamedQueryId = (typeof NAMED_QUERY_IDS)[number];

export const ANALYTICS_METRIC_IDS = ['salesClosed', 'salesCredits', 'quoteLines', 'releaseLines', 'openForms', 'conversion', 'projects', 'pipeline', 'orderLines', 'invoiceLines'] as const;

export type AnalyticsMetricId = (typeof ANALYTICS_METRIC_IDS)[number];

/** The metric each named query reads; a `cloud_run/analytics` test ties it to `NAMED_QUERIES`. */
export const NAMED_QUERY_METRICS = {
  salesClosedByPerson: 'salesClosed',
  salesClosedByTeam: 'salesClosed',
  salesClosedTotal: 'salesClosed',
  salesClosedByMonth: 'salesClosed',
  bookedSeries: 'salesClosed',
  ordersWrittenSeries: 'salesCredits',
  quotesIssuedSeries: 'salesCredits',
  quoteCountAndValueByKind: 'quoteLines',
  quoteValueTotal: 'quoteLines',
  quoteValueByMonth: 'quoteLines',
  quoteValueByPerson: 'quoteLines',
  quoteValueByTeam: 'quoteLines',
  releaseValueByState: 'releaseLines',
  openQuotesAndBuysByProject: 'openForms',
  conversionByKind: 'conversion',
  projectsByStage: 'projects',
  pipelineByStage: 'pipeline',
  orderLinesTotals: 'orderLines',
  orderLinesList: 'orderLines',
  invoiceTotals: 'invoiceLines',
  receivableAging: 'invoiceLines',
  invoiceList: 'invoiceLines',
  overdueInvoicesBySummaryScope: 'invoiceLines',
} as const satisfies Record<NamedQueryId, AnalyticsMetricId>;

/** The open stages a project can be at, lowest rank first, each named by its stage list key; `projectsByStage` stages an active project at one of these or InDesign. */
export const ANALYTICS_OPEN_PROJECT_STAGES = ['quote', 'buy', 'order', 'reconciliation'] as const satisfies readonly (keyof AnalyticsParameters['stages'])[];

/** Every stage `projectsByStage` returns, lowest sort rank first: the outcomes InDesign, Lost and Complete, then the open stages. */
export const ANALYTICS_PROJECT_STAGES = ['InDesign', 'Lost', 'Complete', ...ANALYTICS_OPEN_PROJECT_STAGES] as const;

export type AnalyticsProjectStage = (typeof ANALYTICS_PROJECT_STAGES)[number];

/** The receivable aging buckets, in days-past-due order; a bucket's index is how many `agingStarts` fall after the due date. */
export const ANALYTICS_AGING_BUCKETS = ['days0To30', 'days31To60', 'days61To90', 'daysOver90'] as const;

export type AnalyticsAgingBucket = (typeof ANALYTICS_AGING_BUCKETS)[number];

/** The grouping sets `overdueInvoicesBySummaryScope` answers, named in each row's `groupingSet` column: per salesperson and the total. */
export const ANALYTICS_SUMMARY_GROUPING_SETS = ['salesperson', 'total'] as const;

/** The column a query that produces one row per team names each row's team in; one row per booked team of the fact row, null for a seller with no team. */
export const ANALYTICS_TEAM_ROW_COLUMN = 'teamId';

export type AnalyticsSummaryGroupingSet = (typeof ANALYTICS_SUMMARY_GROUPING_SETS)[number];

/** The bucket width and grouping a series entry takes; no other entry declares them. */
export const NAMED_QUERY_SERIES_PARAMETER_ENUMS = {
  grain: ['day', 'week', 'month', 'quarter'],
  group: ['all', 'salesperson', 'team'],
} as const;

export type AnalyticsSeriesGrain = (typeof NAMED_QUERY_SERIES_PARAMETER_ENUMS.grain)[number];

export type AnalyticsSeriesGroup = (typeof NAMED_QUERY_SERIES_PARAMETER_ENUMS.group)[number];

export type NamedQueryParameters = { period: AnalyticsPeriod; grain?: AnalyticsSeriesGrain; group?: AnalyticsSeriesGroup };

/** The closed enum an entry declares for each parameter it takes: always the period, the series keys only on series entries. */
export type NamedQueryParameterEnums = { readonly period: readonly AnalyticsPeriod[]; readonly grain?: readonly AnalyticsSeriesGrain[]; readonly group?: readonly AnalyticsSeriesGroup[] };

export const NAMED_QUERY_PARAMETER_ENUMS = { period: ANALYTICS_PERIODS } as const satisfies NamedQueryParameterEnums;

export type NamedQueryRequest = { query: NamedQueryId; parameters: NamedQueryParameters; level: AnalyticsScopeLevel };

/** A period resolved to half-open bounds `[from, to)` in the configured calendar. */
export type ResolvedAnalyticsPeriod = { period: AnalyticsPeriod; from: string; to: string; label: string };

/** The `fact_builds` row a result read for one of its declared fact tables. */
export type FactBuildProvenance = { factTable: FactTableId; buildId: string; generation: number; cutTime: string; builtAt: string };

export type NamedQueryCell = string | number | boolean | null;

/** A result column a missing role binding refuses: null in every row; `key` names the binding. */
export type NamedQueryRefusedColumn = { column: string; refusal: Extract<AnalyticsRefusalCode, 'notConfigured'>; key: string };

export type NamedQueryResult = {
  query: NamedQueryId;
  parameters: NamedQueryParameters;
  level: AnalyticsScopeLevel;
  period: ResolvedAnalyticsPeriod;
  snapshotId: number;
  builds: FactBuildProvenance[];
  rows: Record<string, NamedQueryCell>[];
  refusedColumns: NamedQueryRefusedColumn[];
};

/** What one agent answer read, persisted with its chat message: never its rows or refused columns, and at most `ANALYTICS_QUERY_PROVENANCE_MAX_BUILDS` builds. */
export type AnalyticsQueryProvenance = Omit<NamedQueryResult, 'rows' | 'refusedColumns'>;

export const ANALYTICS_QUERY_PROVENANCE_MAX_BUILDS = 3;

export const ANALYTICS_REFUSAL_CODES = ['invalidRequest', 'forbiddenLevel', 'noTeam', 'notConfigured', 'factTableOutdated'] as const;

export type AnalyticsRefusalCode = (typeof ANALYTICS_REFUSAL_CODES)[number];

/** The body `/query` answers a refused request with: the code, never the refusal's message; `key` names the missing setting or binding of a `notConfigured`, and is `query` for an `invalidRequest` naming an id the service does not know; absent otherwise. */
export type AnalyticsQueryRefusal = { refusal: AnalyticsRefusalCode; key?: string };

export const ANALYTICS_QUERY_ROUTE = '/query';

export const ANALYTICS_USER_ID_TOKEN_HEADER = 'x-user-id-token';
